// src/services/TenantUrlHelper.js
class TenantUrlHelper {
  
  // Detect if we're in development (localhost)
  static isDevelopment() {
    return window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1' ||
           window.location.hostname.includes('localhost');
  }

  // Extract tenant info from subdomain or return null if not found
  static getTenantFromSubdomain() {
    const hostname = window.location.hostname;
    
    // Skip if we're in development
    if (this.isDevelopment()) {
      return null;
    }

    // Extract subdomain (everything before the main domain)
    // Example: tms.reliefRoster.com -> tms
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      return parts[0]; // Return the subdomain
    }
    
    return null;
  }

  // Get tenant ID from URL param or subdomain
  static getCurrentTenantId(urlTenantId = null) {
    // In development, use URL parameter
    if (this.isDevelopment()) {
      return urlTenantId;
    }
    
    // In production, use subdomain
    const subdomain = this.getTenantFromSubdomain();
    if (subdomain) {
      // You'll need to map subdomain to tenant ID
      // This could be done via API call or stored mapping
      return this.mapSubdomainToTenantId(subdomain);
    }
    
    return urlTenantId; // Fallback to URL param
  }

  // Map subdomain to tenant ID (you'll need to implement this mapping)
  static mapSubdomainToTenantId(subdomain) {
    // This could be:
    // 1. A stored mapping object
    // 2. An API call to get tenant ID from subdomain
    // 3. The subdomain itself if you use subdomain as tenant ID
    
    // For now, return the subdomain as tenant ID
    // You might need to modify this based on your tenant structure
    return subdomain;
  }

  // Generate the appropriate registration URL based on environment
  static getRegistrationUrl(type, tenantId) {
    if (this.isDevelopment()) {
      // Development: use ugly URL with tenant ID
      return `/${type}-register/${tenantId}`;
    } else {
      // Production: use clean URL without tenant ID
      return `/${type}-register`;
    }
  }

  // Generate volunteer registration URL
  static getVolunteerRegistrationUrl(tenantId) {
    return this.getRegistrationUrl('volunteer', tenantId);
  }

  // Generate substitute registration URL
  static getSubstituteRegistrationUrl(tenantId) {
    return this.getRegistrationUrl('substitute', tenantId);
  }

  // Get the full public URL for sharing
  static getPublicRegistrationUrl(type, tenantId, subdomain = null) {
    const protocol = window.location.protocol;
    const port = window.location.port ? `:${window.location.port}` : '';
    
    if (this.isDevelopment()) {
      // Development: localhost with tenant ID
      return `${protocol}//${window.location.hostname}${port}/${type}-register/${tenantId}`;
    } else {
      // Production: use subdomain
      const domain = this.getMainDomain();
      const subdomainToUse = subdomain || this.getSubdomainFromTenantId(tenantId);
      return `${protocol}//${subdomainToUse}.${domain}/${type}-register`;
    }
  }

  // Get the main domain (reliefRoster.com)
  static getMainDomain() {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    
    if (parts.length >= 2) {
      return parts.slice(-2).join('.'); // Get last 2 parts (domain.com)
    }
    
    return 'reliefRoster.com'; // Fallback
  }

  // Convert tenant ID back to subdomain (you'll implement based on your mapping)
  static getSubdomainFromTenantId(tenantId) {
    // This is the reverse of mapSubdomainToTenantId
    // You'll implement based on how you store the mapping
    return tenantId; // Simplified - assuming tenantId is the subdomain
  }

  // Check if current domain matches expected tenant
  static validateTenantAccess(expectedTenantId) {
    const currentTenantId = this.getCurrentTenantId();
    return currentTenantId === expectedTenantId;
  }
}

export default TenantUrlHelper;